import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { Title, useTranslate } from 'react-admin';
import platformSettingsService from '../services/platformSettingsService';
import IssuerTab from '../components/PlatformSettings/IssuerTab';
import DatevTab from '../components/PlatformSettings/DatevTab';
import FeesTab from '../components/PlatformSettings/FeesTab';
import JobsTab from '../components/PlatformSettings/JobsTab';
import PayPalTab from '../components/PlatformSettings/PayPalTab';
import EnvironmentTab from '../components/PlatformSettings/EnvironmentTab';
import type { PlatformSettings } from '../types';

export const TABS = ['issuer', 'datev', 'fees', 'jobs', 'paypal', 'environment'] as const;
export type PlatformSettingsTab = typeof TABS[number];

/**
 * Operator-Seite "Plattform-Einstellungen": Angaben, die bisher je Umgebung per Env gesetzt
 * werden mussten. Datenbankwert vor Env-Fallback; Secrets bleiben Env (nur Status).
 */
const PlatformSettingsPage: React.FC = () => {
    const translate = useTranslate();
    const [tab, setTab] = useState<PlatformSettingsTab>('issuer');
    const [data, setData] = useState<PlatformSettings | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            setData(await platformSettingsService.get());
        } catch (err: any) {
            setError(err?.response?.data?.error ?? translate('platformSettings.loadError'));
        }
    }, [translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box>
            <Title title={translate('platformSettings.title')} />
            <h1 className="oa-page-title">{translate('platformSettings.title')}</h1>
            <Typography variant="body1" sx={{ mb: 2, color: 'var(--oa-text-2)', maxWidth: 760 }}>{translate('platformSettings.intro')}</Typography>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label={translate('platformSettings.title')}
                sx={{ mb: 3, borderBottom: '1px solid var(--oa-divider)' }}
            >
                {TABS.map((t) => <Tab key={t} value={t} label={translate(`platformSettings.tabs.${t}`)} data-testid={`tab-${t}`} />)}
            </Tabs>

            {error && <Alert severity="error">{error}</Alert>}
            {!error && !data && <CircularProgress size={28} aria-label={translate('platformSettings.loading')} />}
            {data && (
                <Box sx={{ maxWidth: 1040 }}>
                    {tab === 'issuer' && <IssuerTab data={data} onChange={setData} />}
                    {tab === 'datev' && <DatevTab data={data} onChange={setData} />}
                    {tab === 'fees' && <FeesTab data={data} onChange={setData} />}
                    {tab === 'jobs' && <JobsTab data={data} onChange={setData} />}
                    {tab === 'paypal' && <PayPalTab data={data} onChange={setData} />}
                    {tab === 'environment' && <EnvironmentTab data={data} />}
                </Box>
            )}
        </Box>
    );
};

export default PlatformSettingsPage;
